import type { CompanyListItem } from '@/types';

/**
 * Mock company data for development and testing
 * This data simulates the response from the backend API
 */
export const mockCompanies: CompanyListItem[] = [
  {
    id: '1',
    name: 'Acme Corporation',
    code: 'ACME',
    status: 'active',
    financialIntegrationStatus: 'connected',
    hrIntegrationStatus: 'connected',
    employeeCount: 125,
    activeProjects: 23,
  },
  {
    id: '2',
    name: 'Beta Limited',
    code: 'BETA',
    status: 'active',
    financialIntegrationStatus: 'not_configured',
    hrIntegrationStatus: 'connected',
    employeeCount: 45,
    activeProjects: 12,
  },
  {
    id: '3',
    name: 'Gamma Industries',
    code: 'GAMMA',
    status: 'setup',
    financialIntegrationStatus: 'error',
    hrIntegrationStatus: 'not_configured',
    employeeCount: 0,
    activeProjects: 0,
  },
  {
    id: '4',
    name: 'Delta Enterprises',
    code: 'DELTA',
    status: 'active',
    financialIntegrationStatus: 'connected',
    hrIntegrationStatus: 'connected',
    employeeCount: 87,
    activeProjects: 18,
  },
  {
    id: '5',
    name: 'Epsilon Solutions',
    code: 'EPSILON',
    status: 'inactive',
    financialIntegrationStatus: 'not_connected',
    hrIntegrationStatus: 'not_connected',
    employeeCount: 32,
    activeProjects: 5,
  },
  {
    id: '6',
    name: 'Zeta Technologies',
    code: 'ZETA',
    status: 'active',
    financialIntegrationStatus: 'connected',
    hrIntegrationStatus: 'error',
    employeeCount: 156,
    activeProjects: 31,
  },
];

/**
 * Simulate API delay for realistic testing
 */
export const delay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Mock service functions that simulate API calls
 */
export const mockCompanyService = {
  /**
   * Get all companies (with simulated delay)
   */
  async getCompanies(): Promise<CompanyListItem[]> {
    await delay(800);
    return [...mockCompanies];
  },

  /**
   * Get a single company by ID
   */
  async getCompany(id: string): Promise<CompanyListItem | undefined> {
    await delay(500);
    return mockCompanies.find(c => c.id === id);
  },

  /**
   * Create a new company
   */
  async createCompany(data: { name: string; code: string; status: string }): Promise<CompanyListItem> {
    await delay(600);
    const newCompany: CompanyListItem = {
      id: String(mockCompanies.length + 1),
      name: data.name,
      code: data.code,
      status: data.status as 'active' | 'inactive' | 'setup',
      financialIntegrationStatus: 'not_configured',
      hrIntegrationStatus: 'not_configured',
      employeeCount: 0,
      activeProjects: 0,
    };
    mockCompanies.push(newCompany);
    return newCompany;
  },

  /**
   * Update a company
   */
  async updateCompany(id: string, data: Partial<CompanyListItem>): Promise<CompanyListItem | undefined> {
    await delay(600);
    const index = mockCompanies.findIndex(c => c.id === id);
    if (index !== -1) {
      mockCompanies[index] = { ...mockCompanies[index], ...data };
      return mockCompanies[index];
    }
    return undefined;
  },

  /**
   * Test connection
   */
  async testConnection(companyId: string, type: 'financial' | 'hr'): Promise<{ success: boolean; message: string }> {
    await delay(1500);
    const company = mockCompanies.find(c => c.id === companyId);
    if (!company) {
      return { success: false, message: 'Company not found' };
    }

    const status = type === 'financial' ? company.financialIntegrationStatus : company.hrIntegrationStatus;
    if (status === 'connected') {
      return { success: true, message: 'Connection successful' };
    }
    return { success: false, message: 'Connection failed - please check credentials' };
  },

  /**
   * Trigger sync
   */
  async triggerSync(companyId: string, type: 'financial' | 'hr'): Promise<{ success: boolean; recordsSynced: number }> {
    await delay(2000);
    const company = mockCompanies.find(c => c.id === companyId);
    if (!company) {
      throw new Error('Company not found');
    }

    const status = type === 'financial' ? company.financialIntegrationStatus : company.hrIntegrationStatus;
    if (status !== 'connected') {
      throw new Error('Integration not connected');
    }

    return { success: true, recordsSynced: Math.floor(Math.random() * 50) + 10 };
  },

  /**
   * Validate company code
   */
  async validateCompanyCode(code: string, excludeId?: string): Promise<boolean> {
    await delay(300);
    const exists = mockCompanies.some(c => 
      c.code.toLowerCase() === code.toLowerCase() && c.id !== excludeId
    );
    return !exists; // Return true if available (not exists)
  },
};
